import Vue from 'vue';
import Router from 'vue-router';
import URLS from './URLS';
import AuthService from './services/AuthService';
import RestX from 'highered-utils/restx';
import { isMobile } from './utils/windowUtils';

// Configure feature toggles
import FeatureToggles from 'highered-utils/feature-toggle';
import buildConfig from './config/config.json';
import CustomProperties from './utils/CustomProperties';
FeatureToggles.useToggles(buildConfig['toggles']);
Vue.use(FeatureToggles);
CustomProperties.setCustomProperties(buildConfig['customProperties']);
Vue.use(CustomProperties);

import { VFairUtils } from '~/views/vfairs/utils';

import i18n from '~/localization/i18n';
import platformConfig from '~/config/config';

Vue.use(Router);

export const IS_PROD = Boolean(window.location.host.match(/gethighered\./));

export const SAAS_DISABLED = !FeatureToggles.toggle('saas');

// ROUTE TOGGLES
export const SHOW_JOBS = FeatureToggles.toggle('pages.findJobs');
export const SHOW_COMPANIES = FeatureToggles.toggle('pages.findCompanies');
export const HAS_LOGIN = FeatureToggles.toggle('logIn.logIn');
export const HAS_FORCED_LOGIN = FeatureToggles.toggle('logIn.forcedLogin');

export const HAS_MY_PAGE = HAS_LOGIN && FeatureToggles.toggle('pages.myPage');
export const HAS_MY_PROFILE = HAS_LOGIN && FeatureToggles.toggle('pages.myProfile');
export const HAS_FAVOURITES = FeatureToggles.toggle('pages.favourites');
export const HAS_ASSESSMENTS = HAS_LOGIN && FeatureToggles.toggle('pages.assessments');
export const HAS_COURSERA = HAS_LOGIN && !SAAS_DISABLED && FeatureToggles.toggle('pages.coursera');
export const HAS_MASTERCLASSES = HAS_LOGIN && FeatureToggles.toggle('pages.masterClasses');
export const HAS_CALENDAR = HAS_LOGIN && FeatureToggles.toggle('pages.calendar');
export const HAS_INTERNAL_RESOURCES = HAS_LOGIN && FeatureToggles.toggle('pages.internalResources');
export const HAS_COUNTRY_GUIDES = HAS_LOGIN && FeatureToggles.toggle('pages.countryGuides');
export const HAS_HAIGUI_LINK = HAS_LOGIN && FeatureToggles.toggle('pages.haiguiLink');

export const HAS_REFER_A_FRIEND = false && HAS_LOGIN && FeatureToggles.toggle('pages.referAFriend');
export const HAS_DOCUMENTS = platformConfig.hasPage('documents', true);

export const PROMOTED_RESOURCE = FeatureToggles.toggle('promotedResource');

export const SHOW_PROFILE_BANNER = HAS_LOGIN && FeatureToggles.toggle('logIn.profileBanner');
export const PROFILE_MODULE = HAS_LOGIN && FeatureToggles.toggle('logIn.profileModule');
export const FEATURE_COMPANIES_MODULE = FeatureToggles.toggle('companies.featuredModule');
export const FEATURE_POSITIONS_MODULE = FeatureToggles.toggle('positions.featuredModule');
export const FEATURE_POSITIONS_IN_LIST = FeatureToggles.toggle('positions.featured');
export const FORCE_VERIFIED_APPLICATION_CLICK =
    HAS_LOGIN && FeatureToggles.toggle('verify.jobApply');

export const VERIFY_MODULE = HAS_LOGIN && FeatureToggles.toggle('verify.verifyModule');

//Masterclass-related verification requirements
const VERIFY_MASTERCLASS =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.resources');

//Internal-resource-related verification requirements
const VERIFY_RESOURCES_OVERVIEW =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.internalResources.overview');

const VERIFY_INTERNAL_RESOURCES_FOLDER =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.internalResources.folder');

const VERIFY_INTERNAL_RESOURCES_PARENT =
    VERIFY_RESOURCES_OVERVIEW &&
    VERIFY_INTERNAL_RESOURCES_FOLDER &&
    FeatureToggles.toggle('verify.requireVerification.internalResources');

//Calendar-related verification requirements
const VERIFY_CALENDAR_OVERVIEW =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.calendar.overview');

const VERIFY_CALENDAR_BOOK =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.calendar.appointmentList');

const VERIFY_CALENDAR_APPOINTMENT_BOOK =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.calendar.appointmentBook');

const VERIFY_CALENDAR_PARENT =
    VERIFY_CALENDAR_OVERVIEW &&
    VERIFY_CALENDAR_BOOK &&
    VERIFY_CALENDAR_APPOINTMENT_BOOK &&
    FeatureToggles.toggle('verify.requireVerification.calendar');

// Full parent verification requirement
export const REQUIRE_VERIFICATION =
    VERIFY_INTERNAL_RESOURCES_PARENT &&
    VERIFY_CALENDAR_PARENT &&
    VERIFY_MASTERCLASS &&
    FeatureToggles.toggle('verify.requireVerification');

export const HAS_VFAIRS = true; //!FeatureToggles.toggle('beta.vfairs');

export const HAS_CHAT = !isMobile() && FeatureToggles.toggle('chat.enabled');

export const HAS_CHAT_PAGE = HAS_LOGIN && FeatureToggles.toggle('pages.chatPage');

export const SHOW_CHAT_IN_COMPANY_LIST =
    HAS_CHAT && FeatureToggles.toggle('chat.availability.companyList');
export const SHOW_CHAT_IN_COMPANY_PROFILE =
    HAS_CHAT && FeatureToggles.toggle('chat.availability.companyProfile');

const vFairProfileTabs = [
    {
        icon: ['fal', 'user-vneck'],
        label: i18n.t('profile.tabs.profile'),
        to: {
            name: 'vfair-profile',
        },
    },
    {
        icon: ['fal', 'files'],
        label: i18n.t('profile.tabs.documents'),
        to: {
            name: 'vfair-profile-documents',
        },
    },
];

export const homePage = {
    path: URLS.findjobs,
    name: 'jobs',
    component: () => import('./views/findjobs/FindJobs'),
};

let routes = [
    homePage,
    {
        // temporal redirect to jobs page, until we have a proper login page
        path: URLS.login,
        redirect: URLS.profile,
    },
    {
        path: URLS.vfairs,
        name: 'vfairs',
        component: () => import('./views/vfairs/list/FindVFairs'),
    },
    {
        // Fallback for previous job urls, can be removed later
        path: URLS.jobDetailLegacy.concat('/:id'),
        redirect: URLS.jobDetail.concat('/:id'),
    },
    {
        path: URLS.jobDetail.concat('/:id'),
        name: 'jobdetail',
        component: () => import('./views/jobdetail/JobDetail'),
        props: true,
    },
    {
        // Fallback for previous resources urls, can be removed later
        path: URLS.toolsLegacy,
        redirect: URLS.resources,
    },
    {
        path: URLS.masterclasses,
        name: 'resources',
        component: () => import('./views/resources/Resources'),
        meta: {
            requiresAuth: true,
        },
        toggle: HAS_MASTERCLASSES,
    },
    {
        // Fallback for previous internal resources urls, can be removed later
        path: URLS.resourcesLegacy,
        redirect: URLS.resources,
    },
    {
        path: URLS.resources,
        name: 'internalresources',
        component: () => import('./views/resources_internal/overview'),
        meta: {
            requiresAuth: true,
        },
        toggle: HAS_MY_PAGE && HAS_INTERNAL_RESOURCES && VERIFY_RESOURCES_OVERVIEW,
    },
    {
        // Fallback for previous internal resources urls, can be removed later
        path: URLS.resourcesLegacy.concat('/:schoolId/:id'),
        redirect: URLS.resources.concat('/:schoolId/:id'),
    },
    {
        path: URLS.resources.concat('/:schoolId/:id'),
        name: 'internalresources-folder',
        component: () => import('./views/resources_internal/folder'),
        meta: {
            requiresAuth: true,
        },
        toggle: HAS_MY_PAGE && HAS_INTERNAL_RESOURCES && VERIFY_INTERNAL_RESOURCES_FOLDER,
    },
    {
        // Fallback for previous internal resources urls, can be removed later
        path: URLS.resourcesLegacy.concat('/:id'),
        redirect: URLS.resources.concat('/:id'),
    },
    {
        path: URLS.resources.concat('/:id'),
        redirect: URLS.resources,
        name: 'internalresources-folder-old',
        toggle: HAS_MY_PAGE && HAS_INTERNAL_RESOURCES && VERIFY_INTERNAL_RESOURCES_FOLDER,
    },
    {
        path: URLS.calendar,
        name: 'calendar-overview',
        redirect: {
            // name: 'calendar-appointments-list',
            name: 'jobs',
        },
    },
    {
        path: URLS.documents,
        name: 'documents-overview',
        component: () => import('./views/documents/Documents'),
        meta: {
            requiresAuth: true,
            requiresVerification: true,
        },
        toggle: HAS_DOCUMENTS,
    },
    {
        path: URLS.cvs,
        name: 'cv-overview',
        component: () => import('./views/documents/Documents'),
        meta: {
            requiresAuth: true,
            requiresVerification: true,
        },
        toggle: HAS_DOCUMENTS,
    },
    {
        path: URLS.calendar.concat(URLS.book),
        name: 'calendar-appointments-list',
        // component: () => import('./views/calendar/appointment_list'),
        redirect: {
            name: 'jobs',
        },
        meta: {
            requiresAuth: true,
        },
        toggle: HAS_MY_PAGE && HAS_CALENDAR && VERIFY_CALENDAR_BOOK,
    },
    {
        path: URLS.plansAndBillingLegacy.concat('*'),
        redirect: URLS.accountSubscriptions,
    },
    {
        path: URLS.account,
        name: 'account',
        component: () => import('./views/account/Account'),
        meta: {
            requiresAuth: true,
        },
        children: [
            {
                path: '/',
                name: 'account-settings',
                component: () => import('./views/account/views/Settings'),
            },
            {
                path: URLS.accountSubscriptions,
                name: 'account-subscriptions',
                component: () => import('./views/account/views/Subscriptions'),
            },
            {
                path: URLS.accountPurchases,
                name: 'account-purchases',
                component: () => import('./views/account/views/Purchases'),
            },
            {
                path: URLS.accountInvoices,
                name: 'account-invoices',
                component: () => import('./views/account/views/Invoices'),
            },
            {
                path: URLS.accountPayments,
                name: 'account-payments',
                component: () => import('./views/account/views/Payment'),
            },
            {
                path: '*',
                redirect: URLS.account,
            },
        ],
    },
    {
        path: URLS.calendar.concat(URLS.book).concat('/:schoolId/:id'),
        name: 'calendar-appointments-book',
        // component: () => import('./views/calendar/appointment_book'),
        redirect: {
            name: 'jobs',
        },
        meta: {
            requiresAuth: true,
        },
        toggle: HAS_MY_PAGE && HAS_CALENDAR && VERIFY_CALENDAR_APPOINTMENT_BOOK,
    },
    {
        // Fallback for previous companies urls, can be removed later
        path: URLS.findcompaniesLegacy,
        redirect: URLS.findcompanies,
    },
    {
        path: URLS.findcompanies,
        name: 'companies',
        component: () => import('./views/findcompanies/FindCompanies'),
    },
    {
        // Fallback for previous companies urls, can be removed later
        path: URLS.findcompaniesLegacy.concat('/:id'),
        redirect: URLS.findcompanies.concat('/:id'),
    },
    {
        path: URLS.findcompanies.concat('/:id'),
        name: 'companydetail',
        component: () => import('./views/companydetail/CompanyDetail'),
    },
    {
        path: URLS.profile,
        name: 'profile',
        component: () => import('./views/profile/Profile'),
        meta: { requiresAuth: true },
        toggle: HAS_MY_PAGE || HAS_MY_PROFILE,
    },
    {
        path: URLS.chat,
        name: 'chat',
        component: () => import('./views/chat/Chat'),
        meta: { requiresAuth: true },
        toggle: HAS_CHAT_PAGE,
    },
    {
        path: URLS.profileDocuments,
        name: 'profile-documents',
        component: () => import('./views/profile/ProfileDocuments'),
        meta: { requiresAuth: true },
        toggle: HAS_MY_PAGE || HAS_MY_PROFILE,
    },
    {
        path: URLS.favorites,
        name: 'favorites',
        component: () => import('./views/favorites/Favorites'),
        meta: { requiresAuth: true },
        toggle: HAS_MY_PAGE,
    },
    {
        path: URLS.assessments,
        name: 'assessments-landing',
        redirect: URLS.assessments.concat(URLS.assessmentCenter),
    },
    {
        path: URLS.careerPlanning,
        name: 'career-planning-landing',
        redirect: URLS.assessments.concat(URLS.assessmentCenter),
    },
    {
        path: URLS.assessments.concat(URLS.assessmentsRedirect),
        name: 'assessments-redirect',
        redirect: URLS.assessments.concat(URLS.assessmentCenter),
    },
    {
        path: URLS.assessments.concat(URLS.assessmentCenter),
        name: 'assessments-center',
        component: () => import('./views/assessmentscenter/AssessmentsCenter'),
        meta: { requiresAuth: true },
        toggle: HAS_ASSESSMENTS,
    },
    {
        path: URLS.tests.concat(URLS.cute),
        name: 'legacy-tests-cute',
        redirect: { name: 'assessments-center' },
    },
    {
        path: URLS.tests.concat('*'),
        name: 'legacy-tests',
        redirect: { name: 'assessments-landing' },
    },

    /* Coursera */
    // {
    //     // Fallback for previous courses urls, can be removed later
    //     path: URLS.courseraLegacy,
    //     redirect: URLS.coursera,
    // },
    // {
    //     path: URLS.coursera,
    //     name: 'coursera-landing',
    //     component: () => import('./views/coursera/CourseraLanding'),
    //     meta: { requiresAuth: true },
    //     toggle: HAS_COURSERA,
    // },
    // {
    //     // Fallback for previous courses urls, can be removed later
    //     path: URLS.courseraCatalogLegacy,
    //     redirect: URLS.courseraCatalog,
    // },
    // {
    //     path: URLS.courseraCatalog,
    //     name: 'coursera-catalog',
    //     component: () => import('./views/coursera/CourseraLanding'),
    //     meta: { requiresAuth: true },
    //     toggle: HAS_COURSERA,
    // },
    // {
    //     // Fallback for previous courses urls, can be removed later
    //     path: URLS.courseraMyCoursesLegacy,
    //     redirect: URLS.courseraMyCourses,
    // },
    // {
    //     path: URLS.courseraMyCourses,
    //     name: 'coursera-my-courses',
    //     component: () => import('./views/coursera/CourseraLanding'),
    //     meta: { requiresAuth: true },
    //     toggle: HAS_COURSERA,
    // },
    // {
    //     // Fallback for previous courses urls, can be removed later
    //     path: URLS.courseraChooseSubscriptionLegacy,
    //     redirect: URLS.courseraChooseSubscription,
    // },
    // {
    //     path: URLS.courseraChooseSubscription,
    //     name: 'coursera-choose-subscription',
    //     component: () => import('./views/coursera/payments/ChooseSubscription'),
    //     meta: { requiresAuth: true },
    //     toggle: HAS_COURSERA,
    // },
    // {
    //     // Fallback for previous courses urls, can be removed later
    //     path: URLS.courseraHowToLegacy,
    //     redirect: URLS.courseraHowTo,
    // },
    // {
    //     path: URLS.courseraHowTo,
    //     name: 'coursera-how-to',
    //     component: () => import('./views/coursera/payments/HowTo'),
    //     meta: { requiresAuth: true },
    //     toggle: HAS_COURSERA,
    // },
    // {
    //     path: '/coursera',
    //     redirect: () => URLS.coursera,
    // },

    {
        path: URLS.emailVerification,
        name: 'emailVerification',
        component: () => import('./views/emailverification/EmailVerification'),
    },
    {
        path: URLS.recoverUser,
        name: 'recoverUser',
        component: () => import('./views/recoveruser/RecoverUser'),
    },
    {
        path: URLS.recoverPassword,
        name: 'recoverPassword',
        component: () => import('./views/recoverpassword/RecoverPassword'),
    },
    {
        path: URLS.redirect, // Is for handling redirection-events, for example oauth-flow
        name: 'redirect',
        component: () => import('./views/redirect/Redirect'),
    },
    {
        path: URLS.aboutGPTW,
        name: 'aboutGPTW',
        component: () => import('./views/aboutGPTW/AboutGPTW'),
        toggle: !IS_PROD,
    },
    {
        path: URLS.activateUser,
        name: 'activateUser',
        component: () => import('./views/activateuser/ActivateUser'),
    },
    {
        // Fallback for previous vfair urls, can be removed later
        path: URLS.vfairsLegacy,
        redirect: URLS.vfairs,
    },
    {
        // Fallback for previous vfair urls, can be removed later
        path: URLS.vfairsLegacy2,
        redirect: URLS.vfairs,
    },
    {
        // Fallback for previous vfair urls, can be removed later
        path: URLS.vfairLegacy.concat('/:id'),
        redirect: URLS.vfair.concat('/:id'),
    },
    {
        // Fallback for previous vfair urls, can be removed later
        path: URLS.vfairLegacy2.concat('/:id'),
        redirect: URLS.vfair.concat('/:id'),
    },
    {
        path: URLS.vfair.concat('/:id', URLS.vfairDetails),
        name: 'vfairdetails',
        component: () => import('./views/vfairs/details/VFairDetails'),
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: URLS.vfair.concat('/:id'),
        name: 'vfair',
        component: () => import('./views/vfairs/vfair/VFair'),
        meta: {
            requiresAuth: true,
        },
        children: [
            {
                // Fallback for previous vfair urls, can be removed later
                path: URLS.vfairLegacy.concat('/:id'),
                redirect: URLS.vfair.concat('/:id').concat('/booths'),
            },
            {
                // Fallback for previous vfair urls, can be removed later
                path: URLS.vfairLegacy2.concat('/:id'),
                redirect: URLS.vfair.concat('/:id').concat('/booths'),
            },
            // {
            //     // Redirect root to booths
            //     path: URLS.vfair.concat('/:id'),
            //     exact: true,
            //     redirect: URLS.vfair.concat('/:id').concat('/booths'),
            // },
            // Booths
            {
                path: URLS.vfair.concat('/:id').concat(URLS.profile),
                name: 'vfair-profile',
                component: () => import('./views/profile/Profile'),
                requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                props: {
                    tabs: vFairProfileTabs,
                },
            },
            {
                path: URLS.vfair.concat('/:id').concat(URLS.profileDocuments),
                name: 'vfair-profile-documents',
                component: () => import('./views/profile/ProfileDocuments'),
                meta: { requiresAuth: true },
                toggle: HAS_MY_PAGE || HAS_MY_PROFILE,
                props: {
                    tabs: vFairProfileTabs,
                },
            },
            {
                path: URLS.vfair.concat('/:id').concat('/booths'),
                name: 'vfair-booths',
                component: () => import('./views/vfairs/vfair/Booths'),
                requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
            },
            {
                path: URLS.vfair.concat('/:id').concat('/booths').concat('/:boothId'),
                name: 'vfair-booth',
                component: () => import('./views/vfairs/vfair/Booth'),
                requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
            },
            // Jobs
            {
                // Fallback for previous vfair urls, can be removed later
                path: URLS.vfairLegacy.concat('/:id').concat('/jobs'),
                redirect: URLS.vfair.concat('/:id').concat('/jobs'),
            },
            {
                // Fallback for previous vfair urls, can be removed later
                path: URLS.vfairLegacy2.concat('/:id').concat('/jobs'),
                redirect: URLS.vfair.concat('/:id').concat('/jobs'),
            },
            {
                path: URLS.vfair.concat('/:id').concat('/jobs'),
                name: 'vfair-jobs',
                component: () => import('./views/vfairs/vfair/Jobs'),
                requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                children: [
                    {
                        // Fallback for previous vfair urls, can be removed later
                        path: URLS.vfairLegacy.concat('/:id').concat('/jobs').concat('/:jobId'),
                        redirect: URLS.vfair.concat('/:id').concat('/jobs').concat('/:jobId'),
                    },
                    {
                        // Fallback for previous vfair urls, can be removed later
                        path: URLS.vfairLegacy2.concat('/:id').concat('/jobs').concat('/:jobId'),
                        redirect: URLS.vfair.concat('/:id').concat('/jobs').concat('/:jobId'),
                    },
                    {
                        path: URLS.vfair.concat('/:id').concat('/jobs').concat('/:jobId'),
                        name: 'vfair-job',
                        component: () => import('./views/vfairs/vfair/Job'),
                        requiresVerification: FeatureToggles.toggle(
                            'verify.requireVerification.vfair'
                        ),
                    },
                ],
            },
            // Webinars
            {
                // Fallback for previous vfair urls, can be removed later
                path: URLS.vfairLegacy.concat('/:id').concat('/events'),
                redirect: URLS.vfair.concat('/:id').concat('/webinars'),
            },
            {
                // Fallback for previous vfair urls, can be removed later
                path: URLS.vfairLegacy2.concat('/:id').concat('/events'),
                redirect: URLS.vfair.concat('/:id').concat('/webinars'),
            },
            {
                // Fallback for previous vfair urls, can be removed later
                path: URLS.vfair.concat('/:id').concat('/events'),
                redirect: URLS.vfair.concat('/:id').concat('/webinars'),
            },
            {
                path: URLS.vfair.concat('/:id').concat('/webinars'),
                name: 'vfair-webinars',
                component: () => import('./views/vfairs/vfair/Webinars'),
                requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                children: [
                    {
                        // Fallback for previous vfair urls, can be removed later
                        path: URLS.vfairLegacy
                            .concat('/:id')
                            .concat('/webinars')
                            .concat('/:webinarId'),
                        redirect: URLS.vfair
                            .concat('/:id')
                            .concat('/webinars')
                            .concat('/:webinarId'),
                    },
                    {
                        // Fallback for previous vfair urls, can be removed later
                        path: URLS.vfairLegacy2
                            .concat('/:id')
                            .concat('/webinars')
                            .concat('/:webinarId'),
                        redirect: URLS.vfair
                            .concat('/:id')
                            .concat('/webinars')
                            .concat('/:webinarId'),
                    },
                    {
                        path: URLS.vfair.concat('/:id').concat('/webinars').concat('/:webinarId'),
                        name: 'vfair-webinar',
                        component: () => import('./views/vfairs/vfair/Webinar'),
                        requiresVerification: FeatureToggles.toggle(
                            'verify.requireVerification.vfair'
                        ),
                    },
                ],
            },
            // Chat
            {
                path: URLS.vfair.concat('/:id').concat('/chat'),
                name: 'vfair-chat',
                component: () => import('./views/vfairs/vfair/Chat'),
                requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
            },
            // Conference
            {
                path: URLS.vfair.concat('/:id').concat('/conference'),
                name: 'vfair-conference',
                component: () => import('./views/vfairs/vfair/Conference.vue'),
                requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
            },
        ],
    },
    {
        // Fallback for previous vfair urls, can be removed later
        path: URLS.vfairsLegacy.concat('/:id'),
        redirect: URLS.vfairs.concat('/:id'),
    },
    {
        // Fallback for previous vfair urls, can be removed later
        path: URLS.vfairsLegacy2.concat('/:id'),
        redirect: URLS.vfairs.concat('/:id'),
    },
    {
        path: URLS.schoolEvents,
        name: 'schoolevents',
        component: () => import('./views/schoolevents/SchoolEvents'),
    },
    {
        path: URLS.schoolEvents.concat('/:id'),
        name: 'schooleventpage',
        component: () => import('./views/schoolevents/SchoolEventPage'),
    },
];

// Remove routes that has been toggled off
routes = routes.filter(r => (typeof r.toggle === 'undefined' ? true : r.toggle));
routes = routes.concat([
    {
        path: '',
        redirect: URLS.profile,
    },
    {
        path: '*',
        name: 'pagenotfound',
        component: () => import('./views/pagenotfound/PageNotFound'),
    },
]);

export function getVFairEventRoute(vFairId, eventId) {
    return URLS.vfair.concat('/').concat(vFairId).concat('/webinars').concat('/').concat(eventId);
}

const router = new Router({
    base: `/${i18n.locale || ''}`, // process.env.BASE_URL,
    mode: 'history',
    scrollBehavior(/*to, from, savedPosition*/) {
        // Check
        return { x: 0, y: 0 };
    },
    routes: routes,
});

router.beforeEach((to, from, next) => {
    const logInModule = RestX.getInterfaces().logInModule;
    logInModule.resetDisabledPopup();

    // Check if user is trying to leave profile-page with unsaved changes
    if (from.path === URLS.profile && AuthService.isLoggedIn()) {
        if (RestX.getInterfaces().unsavedChanges.hasUnsavedChanges()) {
            RestX.getInterfaces().unsavedChanges.setRedirect(to.path);
            RestX.getInterfaces().unsavedChanges.toggleOpen();
        } else {
            next();
        }
    } else if (to.matched.some(record => record.meta.requiresAuth)) {
        // Check if authenticated (there is a token)
        if (!AuthService.isLoggedIn()) {
            RestX.getInterfaces().logInModule.toggleOpen();
            RestX.getInterfaces().logInModule.setRedirect(to.path);
            RestX.getInterfaces().logInModule.setThen(() => next());
        } else {
            next();
        }
    } else {
        next(); // Always have to call next()
    }
});

router.afterEach((to, from) => {
    RestX.getInterfaces().university.connection.get({ forceUpdate: true });

    const logInModule = RestX.getInterfaces().logInModule;
    if (logInModule.shouldOpenOnNewPage()) {
        logInModule.setHelperText(i18n.t('general.dialogs.helperTexts.logInToAccessMoreFeatures'));
        logInModule.setOpen(true);
        logInModule.setShouldOpenOnNewPage(false);
    }

    // If we are leaving a vfair route, emit an event
    const isLeavingVFair =
        from.matched.some(record => record.name === 'vfair' || record.name === 'vfairdetails') &&
        !to.matched.some(record => record.name === 'vfair' || record.name === 'vfairdetails');
    if (isLeavingVFair) {
        window.dispatchEvent(
            new CustomEvent(VFairUtils.vfairModeEventName, {
                detail: VFairUtils.getVFairModeEventPayload(false),
            })
        );
    }
});

export default router;
